/**
 * Amplitude helpers for GG+ membership analytics.
 * Keeps event names + property shapes in one place (see GG_PLUS_PRODUCT_PLAN.md).
 */
import { logEvent } from './analytics';

const ATTRIBUTION_KEYS = [
  'source',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

function log(event, properties = {}) {
  logEvent(event, properties);
}

/** Keep source + UTM fields when present; ignore anything else. */
function pickAttribution(input = {}) {
  const out = {};
  ATTRIBUTION_KEYS.forEach((key) => {
    if (input[key]) out[key] = input[key];
  });
  return out;
}

/** Membership page loaded (checkout or manage view). */
export function logGgPlusMembershipPageViewed({ source, view, level, ...rest } = {}) {
  log('gg_plus_membership_page_viewed', {
    ...pickAttribution({ source, ...rest }),
    ...(view ? { view } : {}),
    ...(level ? { level } : {}),
  });
}

/** User tapped an upsell CTA toward membership. */
export function logGgPlusUpgradeClicked({ source, plan_id } = {}) {
  log('gg_plus_upgrade_clicked', {
    ...(source ? { source } : {}),
    ...(plan_id != null ? { plan_id } : {}),
  });
}

/** User moved the pay-what-you-want slider. */
export function logGgPlusPriceSelected({ price, source, ...rest } = {}) {
  log('gg_plus_price_selected', {
    price,
    ...pickAttribution({ source, ...rest }),
  });
}

/** Stripe Checkout session created — user leaving app to pay. */
export function logGgPlusCheckoutStarted({ price, source, trial_eligible, ...rest } = {}) {
  log('gg_plus_checkout_started', {
    price,
    ...pickAttribution({ source, ...rest }),
    ...(trial_eligible != null ? { trial_eligible: !!trial_eligible } : {}),
  });
}

/** Checkout succeeded and entitlements show trial. */
export function logGgPlusTrialStarted({ price, source, ...rest } = {}) {
  log('gg_plus_trial_started', {
    ...(price != null ? { price } : {}),
    ...pickAttribution({ source, ...rest }),
  });
}

/** Checkout succeeded without trial (returning subscriber). */
export function logGgPlusSubscriptionStarted({ price, source, ...rest } = {}) {
  log('gg_plus_subscription_started', {
    ...(price != null ? { price } : {}),
    ...pickAttribution({ source, ...rest }),
  });
}

/** Manage action — portal PM update, reactivate, or hub link. */
export function logGgPlusManageClicked({ action, source } = {}) {
  log('gg_plus_manage_clicked', {
    ...(action ? { action } : {}),
    ...(source ? { source } : {}),
  });
}

/** User opened Stripe Portal cancel flow. */
export function logGgPlusCancelClicked({ source } = {}) {
  log('gg_plus_cancel_clicked', {
    ...(source ? { source } : {}),
  });
}

/** Generic GG+ upsell surface became visible (e.g. home dashboard card). */
export function logGgPlusFeatureGateViewed({ source, plan_id } = {}) {
  log('gg_plus_feature_gate_viewed', {
    ...(source ? { source } : {}),
    ...(plan_id != null ? { plan_id } : {}),
  });
}

/** Monthly gifts upsell gate visible (plan card, gifting setup, etc.). */
export function logGgPlusMonthlyGiftsGateViewed({ source, plan_id } = {}) {
  log('gg_plus_monthly_gifts_gate_viewed', {
    ...(source ? { source } : {}),
    ...(plan_id != null ? { plan_id } : {}),
  });
}

/** Gift matching upsell gate visible on plan page or direct URL block. */
export function logGgPlusGiftMatchingGateViewed({ source, plan_id } = {}) {
  log('gg_plus_gift_matching_gate_viewed', {
    ...(source ? { source } : {}),
    ...(plan_id != null ? { plan_id } : {}),
  });
}

/** past_due messaging shown — payment failed, features paused. */
export function logGgPlusPaymentIssueViewed({ source } = {}) {
  log('gg_plus_payment_issue_viewed', {
    ...(source ? { source } : {}),
  });
}

/** Pass properties for analytics */
function passProperties({
  pass_id,
  source_type,
  source_id,
  referrer_user_id,
  duration_months,
} = {}) {
  const out = {};
  if (pass_id != null) out.pass_id = pass_id;
  if (source_type) out.source_type = source_type;
  if (source_id) out.source_id = source_id;
  if (referrer_user_id != null) out.referrer_user_id = referrer_user_id;
  if (duration_months != null) out.duration_months = duration_months;
  return out;
}

/** Preview returned, including an unknown token (no pass id). */
export function logGgPlusPassOpened(props = {}) {
  log('ggplus_pass_opened', passProperties(props));
}

/** Claim button tapped. Fired once per tap, including the logged-out tap that starts signup. */
export function logGgPlusPassClaimStarted(props = {}) {
  log('ggplus_pass_claim_started', passProperties(props));
}

/** Redeem succeeded. */
export function logGgPlusPassRedeemed(props = {}) {
  log('ggplus_pass_redeemed', passProperties(props));
}

/** Redeem waited or failed. `reason` is wait, self, unavailable, or error. */
export function logGgPlusPassRedemptionFailed({ reason, ...props } = {}) {
  log('ggplus_pass_redemption_failed', {
    ...passProperties(props),
    ...(reason ? { reason } : {}),
  });
}

/** Member copied one of their two share links. */
export function logGgPlusPassShareLinkCopied(props = {}) {
  log('ggplus_pass_share_link_copied', passProperties(props));
}
